import { getSocialMediaText } from '@anytag/shared-utils';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { FormAction, SupportedSocialMediaType } from '@anytag/shared-types';
import { BaseModal, Button, ButtonProps, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { SocialAccountType, namedOperations, useUpdateSocialUsernameMutation } from '@anytag/graphql';
import { SocialMediaInputField, SocialMediaInputFieldProps } from '../../../../shared';
import { EditUsernameSchema, EditUsernameFormValues } from './schema-types';

export interface EditAccountButtonProps {
  influencerId: number;
  socialAccountId: number;
  socialMedia: keyof typeof SupportedSocialMediaType;
  buttonProps?: Pick<ButtonProps, 'tooltip' | 'disabled'>;
  inputProps: Pick<SocialMediaInputFieldProps, 'title' | 'placeholder'>;
}
export const EditAccountButton = ({
  socialMedia,
  buttonProps,
  influencerId,
  socialAccountId,
  inputProps: { title, placeholder }
}: EditAccountButtonProps) => {
  const editModal = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callUpdateSocialUsername } = useUpdateSocialUsernameMutation({
    refetchQueries: [namedOperations.Query.InfluencerUpdateDetailV2]
  });

  const socialMediaName = getSocialMediaText(socialMedia);

  const handleSubmit: FormAction<EditUsernameFormValues>['onSubmit'] = async ({ username }) => {
    try {
      await callUpdateSocialUsername({
        variables: { input: { username, influencerId, socialAccountId, socialMedia: socialMedia as SocialAccountType } }
      });

      enqueueSnackbar(t('Username has been updated'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message || 'failedToCreate'), { variant: 'error' });
    } finally {
      editModal.close();
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        variant="outline"
        onClick={editModal.open}
        prefixIcon={{ icon: 'pencil', size: 16 }}
        tooltip={{ help: t('Tooltip.Edit Username'), ...buttonProps?.tooltip }}
      />

      <BaseModal
        open={editModal.status}
        onClose={editModal.close}
        title={t(`Edit ${socialMediaName} account`)}
        description={
          <>
            {t(`Change your ${socialMediaName} account`)}
            <br />
            {t('It will overwrite your account, so your previous data will remain')}
          </>
        }
        asChild
      >
        <Form.Provider<EditUsernameFormValues>
          onSubmit={handleSubmit}
          zodSchema={EditUsernameSchema}
          defaultValues={{ username: '' }}
        >
          {({ values, formState }) => (
            <>
              <ModalBody>
                <SocialMediaInputField<keyof EditUsernameFormValues>
                  title={title}
                  name="username"
                  className="mt-2"
                  socialMedia={socialMedia}
                  placeholder={placeholder || t<string>('enter username')}
                  autoFocus
                />
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: editModal.close }}
                submitButton={{
                  type: 'submit',
                  title: t('Button.Save'),
                  disabled: !values.username,
                  loading: formState.isSubmitting
                }}
              />
            </>
          )}
        </Form.Provider>
      </BaseModal>
    </>
  );
};
